<template>
    <div class="animated fadeIn">
        <div class="kgr-row">
            <div class="kgr-postfix kgr-postfix--gutters kgr-postfix--top kgr-row">
                <div class="kgr-postfix__fluid">
                    <kgr-kindergarten-group-select
                        id="news-group-select"
                        v-model="group"
                        :kindergarten-id="kindergartenId"
                    />
                </div>
                <div
                    v-if="hasGeneralWriteAccess"
                    class="kgr-postfix__fixed"
                >
                    <kgr-batch-delete-buttons
                        selector-class="news-select"
                        delete-path="/kindergartens/news"
                    />
                </div>
                <div
                    v-if="hasGeneralWriteAccess"
                    class="kgr-postfix__fixed"
                >
                    <a
                        class="kgr-button kgr-button--accent"
                        :href="createUrl"
                    >
                        <kgr-icon
                            icon-class="add"
                            small
                            circle
                            highlight
                            class="kgr-button__icon kgr-button__icon--left"
                        />
                        <span>{{ $t('News.addNew') }}</span>
                    </a>
                </div>
            </div>
            <div
                v-show="loading"
                class="kgr-row"
            >
                <div
                    class="kgr-row kgr-text--centered"
                >
                    <kgr-loading />
                </div>
            </div>
            <div
                v-show="!loading"
                class="kgr-row"
            >
                <div class="kgr-grid kgr-grid--inline kgr-grid--gutters kgr-grid--bigGutters kgr-row">
                    <div
                        v-for="news in newsList"
                        :key="news.id"
                        class="kgr-grid__cell"
                        style="width: 350px"
                    >
                        <article class="kgr-card kgr-backgroundColor kgr-backgroundColor--white">
                            <div
                                v-if="news.image"
                                class="kgr-card__image kgr-card__image--full kgr-card__image--contain kgr-card__image--95to64"
                                :style="'background-image: url('+getImageUrl(news.id, news.image)+');'"
                            >
                            </div>
                            <div class="kgr-card__subtitle">
                                <span
                                    v-for="kindergartenGroup in news.groups"
                                    :key="kindergartenGroup.id"
                                >
                                    {{ kindergartenGroup.name }}<br />
                                </span>
                                <span v-if="news.groups.length === 0">
                                    {{ $t('Für alle Gruppen') }}
                                </span>
                            </div>

                            <h3 class="kgr-card__name">
                                <template v-if="isLoading(news)">
                                    <kgr-loading inline />
                                </template>
                                <template v-else>
                                    <a :href="showUrl(news)">
                                        {{ !isOriginal(news.id) && translateTo && news.translations?.title ? news.translations.title : news.title }}
                                    </a>
                                    <kgr-publication-icon
                                        v-if="!hasRoleParent"
                                        :clearing="news.clearing"
                                    />
                                </template>
                            </h3>

                            <div class="kgr-card__contents">
                                <template v-if="isLoading(news)">
                                    <kgr-loading inline />
                                </template>
                                <template v-else>
                                    {{ !isOriginal(news.id) && translateTo && news.translations?.teaser ? news.translations.teaser : news.teaser }}
                                </template>
                            </div>

                            <div class="kgr-card__footer">
                                <div class="kgr-card__footerContents kgr-postfix kgr-postfix--center kgr-postfix--between">
                                    <div class="kgr-postfix__fixed">
                                        <label
                                            v-if="news.hasWriteAccess"
                                            class="kgr-checkbox--inline"
                                        >
                                            <input
                                                type="checkbox"
                                                style="display: none"
                                                class="kgr-checkbox__control news-select"
                                                :value="news.id"
                                            />
                                            <span class="kgr-checkbox__label"></span>
                                        </label>
                                    </div>
                                    <div class="kgr-postfix__fluid">
                                        {{ getParsedDate(news.publishedAt) }}
                                    </div>
                                    <div class="kgr-postfix__fixed">
                                        <SphIcon
                                            v-if="news.hasWriteAccess"
                                            size="md"
                                            :key="news.id + news.topNews"
                                            :icon="Star"
                                            :class="{ 'top-news-active': news.topNews }"
                                            :title="news.topNews ? $t('Als Top-News entfernen') : $t('Als Top-News markieren')"
                                            style="cursor: pointer;"
                                            @click="patchTopNews(news)"
                                        />
                                        <SphIcon
                                            size="md"
                                            :icon="getCalendarIconConfig(news).icon"
                                            :title="getCalendarIconConfig(news).title"
                                        />
                                        <kgr-icon
                                            v-if="translateTo"
                                            icon-class="globe"
                                            :title="isOriginal(news.id) ? $t('Übersetzung anzeigen' ) : $t('Original Text anzeigen')"
                                            small
                                            hover-accent
                                            @click="toggleOriginal(news.id)"
                                        />
                                        <a
                                            v-if="news.hasWriteAccess"
                                            :href="editUrl(news)"
                                            :title="$t('News.modifyEntry')"
                                        >
                                            <kgr-icon
                                                icon-class="edit"
                                                small
                                                hover-accent
                                            />
                                        </a>
                                        <a
                                            :href="showUrl(news)"
                                            :title="$t('more')"
                                        >
                                            <kgr-icon
                                                icon-class="visibility-on"
                                                small
                                                hover-accent
                                            />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </article>
                    </div>
                </div>
            </div>
        </div>
        <div class="kgr-row kgr-text--centered">
            <kgr-paginator
                v-if="showPagination"
                v-model="pagination"
                :length="totalNewsCount"
                :show-limit-options="false"
                show-pages
            />
        </div>
    </div>
</template>

<script>
import { CalendarCheck, CalendarClock, CalendarX, Star } from 'lucide';
import { format, parseISO} from '@legacy/js/date/date-fns.js';
import BatchDeleteButtons from '../../components/kindergarten/BatchDeleteButtons.vue';
import Icon from '../../components/common/icon/Icon.vue';
import KindergartenGroupSelect from '../../components/common/form/select/KindergartenGroupSelect.vue';
import Loading from '../../components/common/Loading.vue';
import NewsRepository from '../../js/repository/NewsRepository.js';
import Paginator from '../../components/common/Paginator.vue';
import PublicationIcon from '../../components/common/icon/PublicationIcon.vue';
import { SphIcon } from '@/components/base/icon/index.ts';
import { getLanguageFromCookie } from '../../js/service/LanguageHelper.js';

export default {
    components: {
        'kgr-icon': Icon,
        'kgr-paginator': Paginator,
        'kgr-loading': Loading,
        'kgr-kindergarten-group-select': KindergartenGroupSelect,
        'kgr-batch-delete-buttons': BatchDeleteButtons,
        'kgr-publication-icon': PublicationIcon,
        'SphIcon': SphIcon,
    },
    props: {
        kindergartenId: {
            type: Number,
            required: true,
        },
        hasGeneralWriteAccess: {
            type: Boolean,
            required: false,
            default: false
        },
        hasRoleParent: {
            type: Boolean,
            required: false,
            default: true
        },
    },
    data() {
        return {
            error: new Error(),
            loading: true,
            newsList: [],
            group: null,
            pagination: {
                offset: 0,
                limit: 10,
            },
            totalNewsCount: 0,
            translateTo: null,
            translatingIds: [],
            showOriginalIds: [],
            CalendarCheck,
            CalendarClock,
            CalendarX,
            Star,
        }
    },
    computed: {
        createUrl() {
            return '/backend/news/new';
        },
        showPagination() {
            return this.newsList.length > 0 && (this.newsList.length > this.pagination.limit -1 || this.pagination.offset > 0);
        },
        untranslatedNews() {
            if (this.translateTo === null) {
                return [];
            }
            return this.newsList.filter(news => {
                const hasTitle = news.title !== null && news.title.trim() !== '';
                const hasTeaser = news.teaser !== null && news.teaser.trim() !== '';
                const hasText = news.text !== null && news.text.trim() !== '';

                const missingTitle = hasTitle && !news.translations?.title;
                const missingTeaser = hasTeaser && !news.translations?.teaser;
                const missingText = hasText && !news.translations?.text;

                return missingTitle || missingTeaser || missingText;

            });
        }
    },
    watch: {
        group() {
            this.translate();
        },
        'pagination.offset': function () {
            if(this.translateTo) {
                this.translate();
            } else {
                this.load();
            }
        },
    },
    created() {
        const savedLang = getLanguageFromCookie();
        if (savedLang && savedLang !== 'de_DE') {
            this.translateTo = savedLang;
        } else {
            this.load();
        }
    },
    methods: {
        showUrl(news) {
            return `/backend/news/${news.id}/show`;
        },
        editUrl(news) {
            return `/backend/news/${news.id}/edit`;
        },
        toggleOriginal(newsId) {
            const index = this.showOriginalIds.indexOf(newsId);
            if (index === -1) {
                this.showOriginalIds.push(newsId);
            } else {
                this.showOriginalIds.splice(index, 1);
            }
        },
        isOriginal(newsId) {
            return this.showOriginalIds.includes(newsId);
        },
        async translate() {
            await this.load();
            const missing = this.untranslatedNews.map(n => n.id);
            if (missing.length === 0) {
                return;
            }

            this.translatingIds = [...missing];

            try {
                await NewsRepository.createTranslations(missing, this.translateTo);

                await Promise.all(
                    missing.map(async (id) => {
                        const updatedItem = await NewsRepository.findById(id, { lang: this.translateTo });
                        const index = this.newsList.findIndex(n => n.id === id);
                        if (index !== -1) {
                            this.$set(this.newsList, index, updatedItem);
                        }
                        this.translatingIds = this.translatingIds.filter(x => x !== id);
                    })
                );
            } catch (e) {
                this.$notify.error('Beim Übersetzen ist ein Fehler aufgetreten.');
                this.translatingIds = [];
            }
        },
        getImageUrl(newsId, imageName) {
            return `/backend/thumbnail/news/${newsId}/news_list_thumb/${imageName}`;
        },
        getParsedDate(date) {
            const parsedDate = parseISO(date);
            return format(parsedDate, 'dd.MM.yyyy HH:mm');
        },
        isLoading(news) {
            return this.translatingIds.indexOf(news.id) !== -1;
        },
        async load() {
            const page = parseInt(this.pagination.offset / this.pagination.limit) + 1;
            this.loading = true;
            return NewsRepository.findAll(this.kindergartenId, {lang: this.translateTo, page: page, perPage: this.pagination.limit, group: this.group?.id})
                .then((news) => {
                    this.newsList = news.data;
                    this.totalNewsCount = news.meta.pagination.total;
                })
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => {
                    this.loading = false;
                });
        },
        getCalendarIconConfig(news) {
            const config = {};
            if (news.published ) {
                config.icon = this.CalendarCheck;
                config.title = news.publishedEnd ? this.$t('Veröffentlichungsdatum endet am {date}', { date: new Date(news.publishedEnd).toLocaleDateString() }) : this.$t('Neuigkeit ist veröffentlicht');
            } else if (news.isNotYetPublished()) {
                config.icon = this.CalendarClock;
                config.title = this.$t('Neuigkeit wird veröffentlicht am {date}', { date: new Date(news.publishedAt).toLocaleString()} );
            } else {
                config.icon = this.CalendarX;
                config.title = this.$t('Neuigkeit ist nicht mehr veröffentlicht');
            }
            return config;
        },
        patchTopNews(news) {
            NewsRepository.patchTopNews(news.id, !news.topNews)
                .then(() => {
                    news.topNews = !news.topNews;
                    this.$notify.success('Neuigkeit wurde aktualisiert')
                })
                .catch(() => this.$notify.error('Neuigkeit konnte nicht aktualisiert werden'))
        },
    },
}
</script>
<style scoped>
.top-news-active :deep(path) {
    fill: currentColor;
}
</style>
